import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';

import client, { httpLink, setPreviewSecret } from 'api/apollo-client';
import {
	Enum_Componentinternaldefaultmoduleoptions_Backgroundcolor,
	Enum_Componentinternaldefaultmoduleoptions_Padding,
	NewsletterData,
	PageInformationFieldsFragment,
	PublicNewsletterFieldsFragment
} from 'api/graphql-types';
import Layout from 'modules/layout/layout';
import { initialState as settingsInitialState } from 'redux/stores/settings';
import IntroHeader from 'modules/intro-header/intro-header';
import { BaseModule } from 'modules/base-module/base-module';
import { Head } from 'modules/head/head';
import { getStrapiParametersFromContext } from 'utils/strapi-parameters';
import { IBasePageProps } from 'types/page';
import { ROUTES } from 'constants/routes';
import { getServerPageGetPublicNewsletter, getServerPageGetManyNewsletters } from 'api/graphql-helper';

const IFrame = dynamic(() => import('components/iframe'));

interface INewsletterPublicPageProps extends IBasePageProps {
	pageInformation?: {
		data: {
			attributes: PageInformationFieldsFragment;
		};
	};
	newsletterPageFields?: PublicNewsletterFieldsFragment[];
	newsletters?: NewsletterData[];
}

const NewsletterPublicPage = (props: INewsletterPublicPageProps): React.JSX.Element => {
	const page = props.newsletterPageFields?.[0].page;
	const { header, seo } = { ...page };

	const headerDefaultOptions = {
		backgroundColor: header?.backgroundColor as Enum_Componentinternaldefaultmoduleoptions_Backgroundcolor,
		padding: 'none' as Enum_Componentinternaldefaultmoduleoptions_Padding
	};

	return (
		<Layout
			typeName={page?.__typename}
			navbar={props?.pageInformation?.data?.attributes?.navbar}
			footer={props?.pageInformation?.data?.attributes?.footer}
		>
			<Head seoModule={seo} seoParent={props?.pageInformation?.data?.attributes?.seo} />
			<BaseModule defaultModuleOptions={headerDefaultOptions}>
				<IntroHeader
					module={{
						title: header?.title || '',
						backgroundColor: header?.backgroundColor,
						excerpt: header?.excerpt || ''
					}}
					slug={ROUTES.NEWSLETTER_PUBLIC.fullPath}
				/>
			</BaseModule>

			<BaseModule defaultModuleOptions={page?.newsletter?.defaultModuleOptions}>
				{/* TODO: Replace with dynamic module or URL; currently not possible due to panicked release */}
				<IFrame
					src="https://nieuwsbrieven.gelderland.nl/x/plugin/?pName=overall_subscribe&pLang=nl&Z=-281056279"
					height={1063}
					seamless
					autoHeight
				/>
			</BaseModule>
		</Layout>
	);
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
	try {
		const { locale, publicationState, draftMode } = getStrapiParametersFromContext(ctx);

		if (publicationState === 'DRAFT') {
			client.setLink(setPreviewSecret(process.env.STRAPI_PREVIEW_SECRET as string).concat(httpLink));
		}

		const newsletterCampaigns = await getServerPageGetManyNewsletters({}, client);

		const result = await getServerPageGetPublicNewsletter(
			{
				variables: {
					locale,
					publicationState
				}
			},
			client
		);
		const { pageInformation, publicNewsletterPage } = result.props.data;

		const { getManyNewsletters } = newsletterCampaigns.props.data;

		if (!getManyNewsletters) {
			return {
				notFound: true,
				props: {
					initialReduxState: {
						settings: {
							...settingsInitialState,
							publicationState,
							draftMode
						}
					}
				}
			};
		}

		return {
			props: {
				pageInformation,
				newsletterPageFields: publicNewsletterPage,
				newsletters: getManyNewsletters || [],
				initialReduxState: {
					settings: {
						...settingsInitialState,
						publicationState,
						draftMode,
						contentType: 'newsletter-public'
					}
				}
			}
		};
	} catch (error: any) {
		// eslint-disable-next-line no-console
		console.error('Error in nieuwsbrief', error);

		return {
			notFound: true
		};
	}
};

export default NewsletterPublicPage;
